import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Rocket, ChevronRight, ChevronDown, ChevronUp, Zap, Car, Bike, Clock } from 'lucide-react';

interface Launch {
  name: string;
  brand: string;
  path: string;
  date: Date;
  type: 'ev' | 'car' | 'bike';
  tag: string;
}

// Upcoming launches — update as new launches are confirmed
const LAUNCHES: Launch[] = [
  {
    name: 'Tata Sierra EV',
    brand: 'Tata',
    path: '/cars/tata/sierra-ev',
    date: new Date(Date.UTC(2026, 4, 19)), // May 19
    type: 'ev',
    tag: 'Price reveal',
  },
  {
    name: 'Honda City Facelift',
    brand: 'Honda',
    path: '/cars/honda/city-facelift-2026-india-launch',
    date: new Date(Date.UTC(2026, 4, 22)), // May 22
    type: 'car',
    tag: 'Launch',
  },
  {
    name: 'Honda ZR-V',
    brand: 'Honda',
    path: '/cars/honda/zr-v-india-2026-launch',
    date: new Date(Date.UTC(2026, 4, 22)), // May 22
    type: 'car',
    tag: 'India debut',
  },
  {
    name: 'Hyundai Stargazer',
    brand: 'Hyundai',
    path: '/cars/hyundai/stargazer',
    date: new Date(Date.UTC(2026, 4, 28)), // May 28
    type: 'car',
    tag: 'Unveil',
  },
];

const TYPE_STYLE = {
  ev: { bg: 'bg-green-500/10', text: 'text-green-400', Icon: Zap },
  car: { bg: 'bg-cat-cars/10', text: 'text-cat-cars', Icon: Car },
  bike: { bg: 'bg-cat-motorcycles/10', text: 'text-cat-motorcycles', Icon: Bike },
} as const;

function daysUntil(date: Date): number {
  const now = Date.now();
  const diff = date.getTime() - now;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function getWeekBounds(): { start: Date; end: Date } {
  const now = new Date();
  const dayOfWeek = now.getUTCDay(); // 0=Sun
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - dayOfWeek));
  const end = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
  return { start, end };
}

export function ThisWeekLaunches() {
  const [collapsed, setCollapsed] = useState(false);

  const { start, end } = getWeekBounds();
  const thisWeek = LAUNCHES.filter((l) => l.date >= start && l.date < end);
  const upcoming = LAUNCHES.filter((l) => l.date >= end).slice(0, 3);
  const items = thisWeek.length > 0 ? thisWeek : upcoming;
  const isThisWeek = thisWeek.length > 0;

  // Hide if no launches within reasonable range
  if (items.length === 0) return null;

  const count = items.length;

  return (
    <div className="rounded-2xl border border-border-primary bg-bg-secondary overflow-hidden">
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="flex w-full items-center gap-2 px-4 py-3 border-b border-border-primary bg-gradient-to-r from-emerald-500/5 to-transparent text-left"
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/10 flex-shrink-0">
          <Rocket className="h-4 w-4 text-emerald-500" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-text-primary text-sm">
            {isThisWeek ? 'This Week in Launches' : 'Upcoming Launches'}
          </h3>
          <span className="text-[10px] text-text-tertiary">
            {count} {count === 1 ? 'launch' : 'launches'} {isThisWeek ? 'this week' : 'coming soon'}
          </span>
        </div>
        {collapsed ? (
          <ChevronDown className="h-4 w-4 text-text-tertiary flex-shrink-0" />
        ) : (
          <ChevronUp className="h-4 w-4 text-text-tertiary flex-shrink-0" />
        )}
      </button>

      {!collapsed && (
        <>
          <div className="divide-y divide-border-primary">
            {items.map((launch) => {
              const days = daysUntil(launch.date);
              const style = TYPE_STYLE[launch.type];
              const IconComp = style.Icon;
              const isToday = days <= 0;

              return (
                <Link
                  key={launch.path}
                  to={launch.path}
                  className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-bg-tertiary group"
                >
                  <div className={`flex h-8 w-8 items-center justify-center rounded-lg flex-shrink-0 ${style.bg}`}>
                    <IconComp className={`h-4 w-4 ${style.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary group-hover:text-primary-400 transition-colors truncate">
                      {launch.name}
                    </p>
                    <p className="text-[11px] text-text-tertiary">{launch.tag}</p>
                  </div>
                  <div className="flex flex-col items-end flex-shrink-0">
                    {isToday ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-400">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        TODAY
                      </span>
                    ) : (
                      <>
                        <span className="text-sm font-bold text-emerald-400 tabular-nums">{days}d</span>
                        <span className="text-[10px] text-text-tertiary flex items-center gap-0.5">
                          <Clock className="h-2.5 w-2.5" />
                          {launch.date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
                        </span>
                      </>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>

          <Link
            to="/?tag=launch"
            className="flex items-center justify-center gap-1 border-t border-border-primary py-3 text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors"
          >
            All launches
            <ChevronRight className="h-4 w-4" />
          </Link>
        </>
      )}
    </div>
  );
}
